import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Logger } from 'utils/logger';
import { VersionToast } from './VersionToast';

const POLL_MS = 12 * 60 * 1000; // backstop poll interval (12 min)
const INITIAL_DELAY_MS = 5 * 60 * 1000; // first check shortly after load (5 min)
const FOCUS_THROTTLE_MS = 60 * 1000; // at most one focus-triggered check per minute
const TOAST_ID = 'app-version-update-listener-toast';

async function fetchServerVersion(): Promise<string | null> {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!response.ok) {
      // A non-OK response often means version.json is missing or being served
      // wrong (e.g. cached/redirected at the edge) — useful for diagnosing why
      // the banner never fires.
      Logger.warn({
        message: 'AppVersion: version.json responded non-OK',
        data: { status: response.status },
        enableInProduction: true,
        source: 'AppVersionUpdateListener',
      });
      return null;
    }

    const data = (await response.json()) as { version?: string };
    const remoteVersion = data.version;

    if (
      remoteVersion == null ||
      typeof remoteVersion !== 'string' ||
      remoteVersion.trim() === ''
    ) {
      Logger.warn({
        message: 'AppVersion: version.json missing/invalid version field',
        data: { received: data },
        enableInProduction: true,
        source: 'AppVersionUpdateListener',
      });
      return null;
    }

    return remoteVersion.trim();
  } catch (error) {
    Logger.warn({
      message: 'AppVersion: version.json fetch failed',
      data: {
        error: error instanceof Error ? error.message : String(error),
      },
      enableInProduction: true,
      source: 'AppVersionUpdateListener',
    });
    return null;
  }
}

/**
 * Polls `/version.json` (served in dev by Vite, emitted at build in `dist/`) and prompts the user
 * when the server version differs from this tab's bundled `__APP_BUILD_VERSION__`.
 *
 * Timing — so the prompt appears promptly and consistently after a deploy:
 * - First check runs ~30s after load (also catches a stale cached bundle vs a fresh server build).
 * - Re-checks whenever the tab regains visibility (throttled to once/min), so a long-open tab is
 *   told soon after the user returns to it.
 * - A 10-minute interval is the backstop.
 * - Also listens for `vite:preloadError:detected` (dispatched by App.tsx when a chunk 404s after a
 *   deploy) to check immediately — so users can finish in-progress work and choose when to refresh
 *   rather than being auto-reloaded mid-task.
 *
 * Re-prompt behaviour:
 * - The guard is keyed to the server version, so a *newer* deploy in the same session re-prompts.
 * - "Later" hides the banner for the current version until a newer build deploys — no timed re-nag.
 */
export const AppVersionUpdateListener = () => {
  const bundledVersion = __APP_BUILD_VERSION__;
  // The server version the user dismissed via "Later". The banner stays hidden
  // for this version and only returns when a newer build changes the value.
  const dismissedVersionRef = useRef<string | null>(null);
  const lastFocusCheckRef = useRef<number>(0);

  useEffect(() => {
    let cancelled = false;
    let intervalId: number | undefined;

    Logger.info({
      message: 'AppVersion: listener mounted',
      data: { bundledVersion },
      enableInProduction: true,
      source: 'AppVersionUpdateListener',
    });

    async function run() {
      const serverVersion = await fetchServerVersion();
      if (cancelled || serverVersion == null) return;
      if (serverVersion === bundledVersion) return;

      const isDismissed = serverVersion === dismissedVersionRef.current;

      // The actionable diagnostic: a real mismatch was detected. `bundledVersion`
      // is what this tab is running (old); `serverVersion` is what's deployed (new).
      Logger.info({
        message: 'AppVersion: update available',
        data: {
          bundledVersion,
          serverVersion,
          isDismissed,
          alreadyShown: toast.isActive(TOAST_ID),
        },
        enableInProduction: true,
        source: 'AppVersionUpdateListener',
      });

      // User chose "Later" for this exact version → stay hidden until a newer build.
      if (isDismissed) return;
      // The prompt for this version is already on screen → don't stack a duplicate.
      if (toast.isActive(TOAST_ID)) return;

      toast.info(
        (props) => (
          <VersionToast
            {...props}
            onLater={() => {
              dismissedVersionRef.current = serverVersion;
            }}
          />
        ),
        {
          toastId: TOAST_ID,
          autoClose: false,
          hideProgressBar: true,
          closeButton: false,
          closeOnClick: false,
          position: 'top-center',
          style: {
            width: '355px',
          },
        },
      );
    }

    function handleVisibility() {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastFocusCheckRef.current < FOCUS_THROTTLE_MS) return;
      lastFocusCheckRef.current = now;
      void run();
    }

    // Immediate check on chunk load failure — no need to wait for the poll interval.
    const handleChunkError = () => void run();
    window.addEventListener('vite:preloadError:detected', handleChunkError);

    const initialTimeout = window.setTimeout(() => {
      void run();
      intervalId = window.setInterval(() => void run(), POLL_MS);
    }, INITIAL_DELAY_MS);

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelled = true;
      window.removeEventListener(
        'vite:preloadError:detected',
        handleChunkError,
      );
      window.clearTimeout(initialTimeout);
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [bundledVersion]);

  return null;
};
